import React, { useEffect, useRef, useState } from 'react';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import { useQuery } from '@tanstack/react-query';
import MarketInsightService from '@app/model/market_insight/service/MarketInsightService';
import { MarketInsightQueryKeys } from '@app/model/market_insight/service/MarketInsightQueryKeys';
import {
	EmptyGraphPlaceholder,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_graph/fragment/EmptyGraphPlaceholder';
import Message from '@app/component/message/Message';
import { BoundingBox, BoundingBoxUtils } from '@app/model/market_insight/BoundingBox';
import MarketInsightOccupancyMap from '@app/model/market_insight/MarketInsightOccupancyMap';
import MarketInsightOccupancyHexagon from '@app/model/market_insight/MarketInsightOccupancyHexagon';
import MarketRegionStateCluster, {
	marketRegionStateClusterBgClasses,
	marketRegionStateClusterColors,
	marketRegionStateClusterNames,
} from '@app/model/market_area/MarketRegionStateCluster';
import { Wrapper } from '@googlemaps/react-wrapper';
import Legend, { LegendGroup } from '@ui/legend/Legend';
import PointerTooltip from '@ui/tooltip/PointerTooltip';

// The 5 levels of the map, in the order of the legend, with the occupancy range of HexagonGridService.calculateCluster.
const LEVELS: { cluster: MarketRegionStateCluster, range: string }[] = [
	{ cluster: MarketRegionStateCluster.VERY_LOW, range: '<20%' },
	{ cluster: MarketRegionStateCluster.LOW, range: '20–35%' },
	{ cluster: MarketRegionStateCluster.NORMAL, range: '35–55%' },
	{ cluster: MarketRegionStateCluster.HIGH, range: '55–75%' },
	{ cluster: MarketRegionStateCluster.VERY_HIGH, range: '≥75%' },
];

const rangeOf = (cluster: MarketRegionStateCluster): string =>
	LEVELS.find(level => level.cluster === cluster)?.range ?? '35–55%';

const LEGEND: LegendGroup[] = [{
	label: 'Occupancy by area',
	info: {
		label: 'How the map works',
		title: 'Occupancy by area',
		content: 'Each hexagon shows the average occupancy of the 5 or more nearest hotels of your market. No hexagon shows one hotel.',
	},
	items: LEVELS.map(level => ({
		label: `${marketRegionStateClusterNames[level.cluster]} ${level.range}`,
		mark: 'square' as const,
		markClassName: marketRegionStateClusterBgClasses[level.cluster],
	})),
}];

// marketAreaId: the market of the market tab; null = the market of the hotel.
const MarketInsightOccupancyMapFragment: React.FunctionComponent<{ statDate: string, marketAreaId: number | null }> = ({ statDate, marketAreaId }) => {

	const { companyHotel } = useSelectedCompanyHotel();

	const { data, isLoading, isSuccess } = useQuery({
		queryFn: () => MarketInsightService.findOccupancyHexagons({ companyHotelId: companyHotel.id, statDate: statDate, marketAreaId: marketAreaId }),
		queryKey: MarketInsightQueryKeys.occupancyHexagons(companyHotel.id, statDate, marketAreaId),
	});

	if (isLoading) return <EmptyGraphPlaceholder />;

	if (isSuccess && data !== null && data !== undefined) {
		if (data.hexagons.length === 0) {
			return <div className="flex h-full items-center justify-center p-6">
				<div className="max-w-md">
					<Message state="info" title="Not enough hotels for an area map">
						An area shows only when it holds 5 or more hotels, so that nobody can see the occupancy of one hotel.
					</Message>
				</div>
			</div>;
		}
		// A new market is a new map: the first view fits the hexagons of that market.
		return <RenderMarketInsightOccupancyMap key={marketAreaId ?? 'own'} occupancyMap={data} />;
	}

	return <Message state="error">Map cannot be displayed!</Message>;

};


const RenderMarketInsightOccupancyMap: React.FunctionComponent<{
	occupancyMap: MarketInsightOccupancyMap
}> = ({ occupancyMap }) => {
	const API_KEY = 'AIzaSyB_C2tX9nDSqsm5eR75uzKq9_gEgQfEq1c';

	return <div className="flex h-full flex-col gap-y-2">
		<div className="px-2 pt-1">
			<Legend groups={LEGEND} />
		</div>
		<Wrapper apiKey={API_KEY} libraries={['geometry']}>
			<MapComponent occupancyMap={occupancyMap} />
		</Wrapper>
	</div>;
};

/**
 * Calculate optimal heading angle based on the center coordinates of the bounding box
 * Uses Athens (38°N, 23°E) as reference where heading: 2 works perfectly
 */
const calculateOptimalHeading = (centerLat: number, centerLng: number): number => {
	// Reference point: Athens, Greece (where heading: 2 is perfect)
	const athensLat = 37.9838;
	const athensLng = 23.7275;
	const athensOptimalHeading = 2;

	// Calculate simple adjustments based on distance from Athens
	const latDiff = centerLat - athensLat;
	const lngDiff = centerLng - athensLng;

	// Simple linear adjustments (very small corrections)
	const latAdjustment = latDiff * 0.02; // Very small adjustment per degree latitude
	const lngAdjustment = lngDiff * 0.01; // Very small adjustment per degree longitude

	// Calculate final heading with minimal adjustments
	const calculatedHeading = athensOptimalHeading + latAdjustment + lngAdjustment;

	// Keep adjustments minimal - clamp to reasonable bounds around the Athens value
	return Math.max(-5, Math.min(10, calculatedHeading));
};

type Hover = { x: number, y: number, hexagon: MarketInsightOccupancyHexagon };

/**
 * The centre, the heading and the zoom that show every hexagon of the box in the map container. The map shows the box
 * turned by the heading, so the zoom fits the turned box.
 */
const fitOf = (box: BoundingBox, container: HTMLDivElement): { center: google.maps.LatLngLiteral, heading: number, zoom: number } => {
	// Get the actual dimensions of the map container
	const mapWidth = container.offsetWidth > 0 ? container.offsetWidth : 800;
	const mapHeight = container.offsetHeight > 0 ? container.offsetHeight : 600;
	const center = BoundingBoxUtils.getCenterCoordinates(box);
	// Dynamically adjust heading based on geographic location to align H3 hexagons
	const heading = calculateOptimalHeading(center.lat, center.lng);
	return { center, heading, zoom: BoundingBoxUtils.calculateOptimalZoom(BoundingBoxUtils.rotate(box, heading), mapWidth, mapHeight) };
};

const MapComponent: React.FunctionComponent<{ occupancyMap: MarketInsightOccupancyMap }> = ({ occupancyMap }) => {
	const boxRef = useRef<HTMLDivElement>(null);
	const ref = useRef<HTMLDivElement>(null);
	const [map, setMap] = useState<google.maps.Map | null>(null);
	const [hover, setHover] = useState<Hover | null>(null);

	useEffect(() => {
		if (ref.current !== null && map === null) {
			const { center, heading, zoom } = fitOf(occupancyMap.boundingBox, ref.current);

			const options: google.maps.MapOptions = {
				zoom: zoom,
				center: center,
				mapId: '71650332a8c41b4f',
				heading: heading,
				// Set tilt to 0 for a top-down view
				tilt: 0,
				isFractionalZoomEnabled: true,
				// The map is locked: the market tabs select the market, so no control, no drag, no zoom and no keys.
				// A plain scroll moves the page.
				disableDefaultUI: true,
				gestureHandling: 'none',
				keyboardShortcuts: false,
				disableDoubleClickZoom: true,
				// A click on a Google place opens an info window, and the info window moves the map.
				clickableIcons: false,
			};

			const newMap = new google.maps.Map(ref.current, options);
			setMap(newMap);
		}
	}, [map, occupancyMap]);

	// The user cannot move the map, so the map fits the hexagons again when its container changes size.
	useEffect(() => {
		const container = ref.current;
		if (map === null || container === null) return;
		const observer = new ResizeObserver(() => {
			const { center, zoom } = fitOf(occupancyMap.boundingBox, container);
			map.setCenter(center);
			map.setZoom(zoom);
		});
		observer.observe(container);
		return () => observer.disconnect();
	}, [map, occupancyMap]);

	useEffect(() => {
		if (map === null) return;
		const polygons = renderHexagons(map, occupancyMap.hexagons, (hexagon, event) => {
			const box = boxRef.current;
			const dom = event.domEvent;
			if (box === null || !('clientX' in dom)) return;
			const rect = box.getBoundingClientRect();
			setHover({ x: dom.clientX - rect.left, y: dom.clientY - rect.top, hexagon });
		}, () => setHover(null));
		return () => polygons.forEach(polygon => polygon.setMap(null));
	}, [map, occupancyMap]);

	return <div ref={boxRef} className="relative min-h-0 flex-1 overflow-hidden rounded-md">
		<div className="absolute inset-0" ref={ref} id={'map'}></div>
		{hover !== null && <PointerTooltip x={hover.x} y={hover.y}
			containerWidth={boxRef.current?.offsetWidth ?? 0} containerHeight={boxRef.current?.offsetHeight ?? 0}
			title={`${marketRegionStateClusterNames[hover.hexagon.cluster]} · ${rangeOf(hover.hexagon.cluster)}`}
			text={`Average of the ${hover.hexagon.hotelCount} nearest hotels`}
			markClassName={marketRegionStateClusterBgClasses[hover.hexagon.cluster]} />}
	</div>;
};


/**
 * Render hexagons on the Google Map: the app colour of the level, a light fill, a white border.
 */
const renderHexagons = (
	map: google.maps.Map,
	hexagons: MarketInsightOccupancyHexagon[],
	onHover: (hexagon: MarketInsightOccupancyHexagon, event: google.maps.MapMouseEvent) => void,
	onLeave: () => void,
): google.maps.Polygon[] => hexagons.map(hexagon => {
	const polygon = new google.maps.Polygon({
		paths: hexagon.vertices.map(vertex => ({ lat: Number(vertex.lat), lng: Number(vertex.lng) })),
		strokeColor: 'white',
		strokeOpacity: 0.7,
		strokeWeight: 1,
		fillColor: marketRegionStateClusterColors[hexagon.cluster],
		fillOpacity: 0.45,
		clickable: true,
		zIndex: 1,
	});
	polygon.addListener('mousemove', (event: google.maps.MapMouseEvent) => onHover(hexagon, event));
	polygon.addListener('mouseout', onLeave);
	polygon.setMap(map);
	return polygon;
});

export default MarketInsightOccupancyMapFragment;
